import React, { useState, useEffect } from 'react';
import { contentAPI, teamsAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';

const PlayersSection = () => {
  const { language } = useLanguage();
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState('all');
  const [playerSearchFilter, setPlayerSearchFilter] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [playersRes, teamsRes] = await Promise.all([
        contentAPI.getPlayers(),
        teamsAPI.getAll()
      ]);

      console.log('Teams Response:', teamsRes);
      console.log('Teams Data:', teamsRes.data);

      // Gérer différents formats de réponse
      const teamsData = teamsRes.data || teamsRes || [];
      const playersData = playersRes.data || playersRes || [];

      setPlayers(playersData);
      setTeams(teamsData);

      console.log('Teams loaded:', teamsData.length);
    } catch (error) {
      console.error('Erreur chargement:', error);
      setPlayers([]);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.id) {
        await contentAPI.updatePlayer(formData.id, formData);
      } else {
        await contentAPI.createPlayer(formData);
      }
      loadData();
      setFormData({});
      alert('Joueur enregistré!');
    } catch (error) {
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer ce joueur?')) {
      try {
        await contentAPI.deletePlayer(id);
        loadData();
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  // Filtrer les joueurs par équipe et recherche
  const filteredPlayers = players.filter(p => {
    const teamMatch = selectedTeamFilter === 'all' || p.team_id === parseInt(selectedTeamFilter);
    const searchMatch = !playerSearchFilter ||
      (p.name || '').toLowerCase().includes(playerSearchFilter.toLowerCase()) ||
      (p.name_ar || '').includes(playerSearchFilter);
    return teamMatch && searchMatch;
  });

  // Trouver le nom de l'équipe
  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === parseInt(teamId));
    if (!team) return language === 'ar' ? 'فريق غير معروف' : 'Équipe inconnue';
    return language === 'ar' ? (team.name_ar || team.name) : team.name;
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div>
      <h1 style={styles.title}>Gestion des Joueurs & Statistiques</h1>

      <div style={styles.card}>
        <h2 style={styles.cardTitle}>
          {formData.id ? 'Modifier' : 'Ajouter'} un joueur
        </h2>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Équipe *</label>
            <select
              value={formData.team_id || ''}
              onChange={(e) => setFormData({ ...formData, team_id: e.target.value })}
              style={styles.input}
              required
            >
              <option value="">Sélectionner une équipe</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.name} {team.group_number ? `(Groupe ${team.group_number})` : ''}
                </option>
              ))}
            </select>
            {teams.length === 0 && (
              <p style={styles.helpText}>
                ⚠️ Aucune équipe disponible. Veuillez d'abord ajouter des équipes.
              </p>
            )}
          </div>
          <input
            type="text"
            placeholder="Nom du joueur"
            value={formData.name || ''}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            style={styles.input}
            required
          />
          <input
            type="text"
            placeholder="اسم اللاعب"
            value={formData.name_ar || ''}
            onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
            style={styles.input}
          />
          <input
            type="text"
            placeholder="Position"
            value={formData.position || ''}
            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
            style={styles.input}
          />
          <input
            type="number"
            placeholder="Numéro"
            value={formData.number || ''}
            onChange={(e) => setFormData({ ...formData, number: e.target.value })}
            style={styles.input}
          />
          <div style={styles.statsRow}>
            <input
              type="number"
              placeholder="Buts"
              value={formData.goals || 0}
              onChange={(e) => setFormData({ ...formData, goals: e.target.value })}
              style={styles.input}
              min="0"
            />
            <input
              type="number"
              placeholder="Cartons jaunes"
              value={formData.yellow_cards || 0}
              onChange={(e) => setFormData({ ...formData, yellow_cards: e.target.value })}
              style={styles.input}
              min="0"
            />
            <input
              type="number"
              placeholder="Cartons rouges"
              value={formData.red_cards || 0}
              onChange={(e) => setFormData({ ...formData, red_cards: e.target.value })}
              style={styles.input}
              min="0"
            />
            <input
              type="number"
              placeholder="Matchs joués"
              value={formData.matches_played || 0}
              onChange={(e) => setFormData({ ...formData, matches_played: e.target.value })}
              style={styles.input}
              min="0"
            />
          </div>
          <div style={styles.formActions}>
            <button type="submit" style={styles.submitBtn}>
              {formData.id ? 'Modifier' : 'Ajouter'}
            </button>
            {formData.id && (
              <button type="button" onClick={() => setFormData({})} style={styles.cancelBtn}>
                Annuler
              </button>
            )}
          </div>
        </form>
      </div>

      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>Liste des joueurs ({filteredPlayers.length})</h2>
          <div style={{ display: 'flex', gap: '15px' }}>
            <input
              type="text"
              placeholder={language === 'ar' ? 'بحث بالاسم...' : 'Rechercher par nom...'}
              value={playerSearchFilter}
              onChange={(e) => setPlayerSearchFilter(e.target.value)}
              style={styles.filterSelect}
            />
            <select
              value={selectedTeamFilter}
              onChange={(e) => setSelectedTeamFilter(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="all">{language === 'ar' ? 'جميع الفرق' : 'Toutes les équipes'}</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{language === 'ar' ? (team.name_ar || team.name) : team.name}</option>
              ))}
            </select>
          </div>
        </div>
        {filteredPlayers.length === 0 ? (
          <div style={styles.emptyState}>
            <p>Aucun joueur trouvé</p>
            <p style={styles.emptyHint}>Ajoutez votre premier joueur ci-dessus</p>
          </div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Joueur</th>
                  <th style={styles.th}>Équipe</th>
                  <th style={styles.th}>Position</th>
                  <th style={styles.th}>N°</th>
                  <th style={styles.th}>⚽ Buts</th>
                  <th style={styles.th}>🟨 CJ</th>
                  <th style={styles.th}>🟥 CR</th>
                  <th style={styles.th}>Matchs</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlayers.map(player => (
                  <tr key={player.id} style={styles.tr}>
                    <td style={styles.td}>
                      <div>{player.name}</div>
                      <div style={styles.arabicText}>{player.name_ar}</div>
                    </td>
                    <td style={styles.td}>{getTeamName(player.team_id)}</td>
                    <td style={styles.td}>{player.position}</td>
                    <td style={styles.td}>{player.number}</td>
                    <td style={styles.td}><strong>{player.goals}</strong></td>
                    <td style={styles.td}>{player.yellow_cards}</td>
                    <td style={styles.td}>{player.red_cards}</td>
                    <td style={styles.td}>{player.matches_played}</td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button onClick={() => setFormData(player)} style={styles.editBtn}>
                          Modifier
                        </button>
                        <button onClick={() => handleDelete(player.id)} style={styles.deleteBtn}>
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  title: { fontSize: '32px', fontWeight: 'bold', marginBottom: '30px' },
  card: { background: 'white', padding: '30px', borderRadius: '15px', marginBottom: '30px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' },
  cardTitle: { fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' },
  form: { display: 'flex', flexDirection: 'column', gap: '15px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontSize: '14px', fontWeight: 'bold', color: '#333' },
  input: { padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '15px' },
  helpText: { fontSize: '13px', color: '#dc3545', margin: '5px 0 0 0' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' },
  formActions: { display: 'flex', gap: '10px' },
  submitBtn: { padding: '15px', background: '#c41e3a', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', flex: 1 },
  cancelBtn: { padding: '15px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' },
  filterSelect: { padding: '10px 15px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', fontWeight: '600', minWidth: '200px' },
  tableContainer: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', fontWeight: 'bold', background: '#f9f9f9' },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '12px' },
  arabicText: { color: '#666', fontSize: '12px', marginTop: '3px' },
  actions: { display: 'flex', gap: '5px' },
  editBtn: { padding: '6px 12px', background: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' },
  deleteBtn: { padding: '6px 12px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', color: '#666' },
  spinner: { width: '50px', height: '50px', border: '5px solid #f3f3f3', borderTop: '5px solid #c41e3a', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  emptyState: { textAlign: 'center', padding: '60px 20px', color: '#999' },
  emptyHint: { fontSize: '14px', marginTop: '10px' }
};

// Ajouter l'animation CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
if (!document.getElementById('players-section-styles')) {
  styleSheet.id = 'players-section-styles';
  document.head.appendChild(styleSheet);
}

export default PlayersSection;
